import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PROFESSIONAL_QUESTIONS_V2, PROFESSIONAL_CONFIG } from '../data/professionalAssessmentV2';
import { STUDENT_QUESTIONS_V2_FULL, STUDENT_CONFIG } from '../data/studentAssessmentV2';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Clock, Shield, Brain, Timer, CheckCircle2, AlertCircle, GraduationCap, Briefcase, Globe, Loader2 } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language options matching report languages - All 20 languages
const ASSESSMENT_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം' },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ' },
  { code: 'or', name: 'Odia', nativeName: 'ଓଡ଼ିଆ' },
  { code: 'as', name: 'Assamese', nativeName: 'অসমীয়া' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', rtl: true },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', rtl: true },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
];

// Configuration - Updated for 85 questions, 30 minutes
const PROFESSIONAL_QUESTIONS_COUNT = PROFESSIONAL_CONFIG.totalQuestions;  // 85
const STUDENT_QUESTIONS_COUNT = STUDENT_CONFIG.totalQuestions;  // 85
const PROFESSIONAL_MINUTES = PROFESSIONAL_CONFIG.timeMinutes;  // 30
const STUDENT_MINUTES = STUDENT_CONFIG.timeMinutes;  // 30

// Fisher-Yates shuffle
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Select random questions with phase coverage
const selectRandomQuestions = (questionPool, count) => {
  const phases = {};
  questionPool.forEach(q => {
    if (!phases[q.phase]) phases[q.phase] = [];
    phases[q.phase].push(q);
  });

  const selected = [];
  const phaseKeys = Object.keys(phases);
  const minPerPhase = Math.floor(count / phaseKeys.length);
  
  phaseKeys.forEach(phase => {
    const shuffledPhase = shuffleArray(phases[phase]);
    const phaseCount = Math.min(minPerPhase, shuffledPhase.length);
    selected.push(...shuffledPhase.slice(0, phaseCount));
  });

  const remaining = count - selected.length;
  if (remaining > 0) {
    const usedIds = new Set(selected.map(q => q.id));
    const unusedQuestions = questionPool.filter(q => !usedIds.has(q.id));
    const shuffledRemaining = shuffleArray(unusedQuestions);
    selected.push(...shuffledRemaining.slice(0, remaining));
  }

  return shuffleArray(selected);
};

// Format time
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const AssessmentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { language: globalLanguage, setLanguage: setGlobalLanguage, t, isRTL } = useLanguage();
  const [phase, setPhase] = useState('intro');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [assessmentId, setAssessmentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(1800); // 30 minutes = 1800 seconds
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(globalLanguage || 'en');
  const [translatedQuestions, setTranslatedQuestions] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  
  // Sync with global language
  useEffect(() => {
    if (globalLanguage && globalLanguage !== selectedLanguage) {
      setSelectedLanguage(globalLanguage);
    }
  }, [globalLanguage]);
  
  // Determine assessment type from URL params
  const assessmentType = searchParams.get('type') || 'professional';
  const isStudent = assessmentType === 'student';
  
  // Select questions based on assessment type - using new V2 question banks
  const questionPool = isStudent ? STUDENT_QUESTIONS_V2_FULL : PROFESSIONAL_QUESTIONS_V2;
  const questionsCount = isStudent ? STUDENT_QUESTIONS_COUNT : PROFESSIONAL_QUESTIONS_COUNT;
  const estimatedMinutes = isStudent ? STUDENT_MINUTES : PROFESSIONAL_MINUTES;
  
  const selectedQuestions = useMemo(
    () => selectRandomQuestions(questionPool, questionsCount),
    [isStudent]
  );
  
  const totalQuestions = selectedQuestions.length;
  const currentQuestion = selectedQuestions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;
  const answeredCount = Object.keys(responses).length;
  const currentAnswer = responses[currentQuestion?.id];

  // Timer countdown effect (30 minutes total)
  useEffect(() => {
    let interval;
    if (isTimerRunning && remainingTime > 0) {
      interval = setInterval(() => {
        setRemainingTime(prev => Math.max(0, prev - 1));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, remainingTime]);

  // Calculate progress-based remaining time estimate
  const avgTimePerQuestion = answeredCount > 0 ? (1800 - remainingTime) / answeredCount : 21;  // ~21 seconds per question for 85 in 30 min
  const remainingQuestions = totalQuestions - answeredCount;
  const estimatedRemainingSeconds = Math.round(remainingQuestions * avgTimePerQuestion);
  const estimatedRemainingMinutes = Math.ceil(estimatedRemainingSeconds / 60);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
  }, [user, navigate]);

  // Translate questions when language changes
  const translateQuestions = async (langCode) => {
    if (langCode === 'en') {
      setTranslatedQuestions(null);
      return;
    }
    
    setIsTranslating(true);
    try {
      const response = await axios.post(`${API}/assessment/translate`, {
        questions: selectedQuestions.map(q => ({
          id: q.id,
          text: q.text,
          options: q.options.map(o => ({ id: o.id, text: o.text }))
        })),
        target_language: langCode
      });
      setTranslatedQuestions(response.data.translated_questions);
      toast.success(`Assessment translated to ${ASSESSMENT_LANGUAGES.find(l => l.code === langCode)?.name || langCode}`);
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Translation not available. Continuing in English.');
      setSelectedLanguage('en');
      setTranslatedQuestions(null);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleLanguageChange = (langCode) => {
    setSelectedLanguage(langCode);
    setGlobalLanguage(langCode);  // Update global language for reports
    if (phase === 'intro') {
      // Will translate when starting
    } else if (phase === 'questions') {
      translateQuestions(langCode);
    }
  };

  // Get display text for current question (translated or original)
  const getQuestionText = (question) => {
    if (translatedQuestions && translatedQuestions[question.id]) {
      return translatedQuestions[question.id].question || translatedQuestions[question.id].text;
    }
    return question.question;
  };

  const getOptionText = (question, option) => {
    if (translatedQuestions && translatedQuestions[question.id]) {
      const translatedOption = translatedQuestions[question.id].options?.find(o => o.id === option.id);
      if (translatedOption) return translatedOption.text;
    }
    return option.text;
  };

  const startAssessment = async () => {
    try {
      setLoading(true);
      
      // Translate questions if non-English language selected
      if (selectedLanguage !== 'en') {
        await translateQuestions(selectedLanguage);
      }
      
      const response = await axios.post(`${API}/assessment/start`, {
        assessment_type: assessmentType,
        language: selectedLanguage
      });
      setAssessmentId(response.data.assessment_id);
      setPhase('questions');
      setIsTimerRunning(true);
    } catch (error) {
      toast.error('Failed to start assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = useCallback((answerId) => {
    setResponses(prev => ({
      ...prev,
      [currentQuestion.id]: answerId,
    }));
  }, [currentQuestion?.id]);

  const nextQuestion = () => {
    if (!responses[currentQuestion.id]) {
      toast.error('Please select an answer before continuing');
      return;
    }

    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      submitAssessment();
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const extractTemporalData = () => {
    const temporalResponses = selectedQuestions
      .filter(q => q.phase === 'temporal_patterns')
      .map(q => ({
        question_id: q.id,
        answer_id: responses[q.id],
        answer_text: q.options.find(o => o.id === responses[q.id])?.text || '',
      }));

    const birthData = { derived_from_patterns: true };

    const seasonQ = temporalResponses.find(r => r.question_id === 'temporal_2');
    if (seasonQ) {
      const seasonMap = {
        'a': { season: 'winter', month_range: '12-2' },
        'b': { season: 'spring', month_range: '3-5' },
        'c': { season: 'summer', month_range: '6-8' },
        'd': { season: 'autumn', month_range: '9-11' },
      };
      const season = seasonMap[seasonQ.answer_id];
      if (season) {
        birthData.season = season.season;
        birthData.month_range = season.month_range;
      }
    }

    const decadeQ = temporalResponses.find(r => r.question_id === 'temporal_3');
    if (decadeQ) {
      const decadeMap = {
        'a': { decade: '1960s-1970s', year_range: '1960-1979' },
        'b': { decade: '1980s', year_range: '1980-1989' },
        'c': { decade: '1990s', year_range: '1990-1999' },
        'd': { decade: '2000s', year_range: '2000-2009' },
      };
      const decade = decadeMap[decadeQ.answer_id];
      if (decade) {
        birthData.decade = decade.decade;
        birthData.year_range = decade.year_range;
      }
    }

    const regionQ = temporalResponses.find(r => r.question_id === 'temporal_6');
    if (regionQ) {
      const regionMap = { 'a': 'North America', 'b': 'Europe', 'c': 'Asia', 'd': 'Other' };
      birthData.region = regionMap[regionQ.answer_id] || 'Unknown';
    }

    const envQ = temporalResponses.find(r => r.question_id === 'temporal_5');
    if (envQ) {
      const envMap = { 'a': 'Metropolitan', 'b': 'Urban', 'c': 'Suburban', 'd': 'Rural' };
      birthData.environment = envMap[envQ.answer_id] || 'Unknown';
    }

    return birthData;
  };

  const submitAssessment = async () => {
    setLoading(true);
    setPhase('processing');
    setIsTimerRunning(false);

    try {
      const formattedResponses = Object.entries(responses).map(([questionId, answerId]) => {
        const question = selectedQuestions.find(q => q.id === questionId);
        const answer = question?.options.find(o => o.id === answerId);
        return {
          question_id: questionId,
          answer_id: answerId,
          answer_text: answer?.text || '',
          weights: answer?.weight || {},
          phase: question?.phase || '',
        };
      });

      const birthData = extractTemporalData();
      birthData.full_name = user?.full_name || '';
      birthData.completion_time_seconds = 300 - remainingTime;

      await axios.post(`${API}/assessment/submit`, {
        assessment_id: assessmentId,
        assessment_type: assessmentType,
        responses: formattedResponses,
        birth_data: birthData,
      });

      const reportResponse = await axios.post(`${API}/report/generate`, {
        assessment_id: assessmentId,
      });

      toast.success('Assessment completed successfully');
      
      // All users now receive reports via email - redirect to Thank You page
      navigate(`/thank-you?report_id=${reportResponse.data.report_id}`);
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to process assessment. Please try again.');
      setPhase('questions');
      setIsTimerRunning(true);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  // Intro Phase
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center px-4" data-testid="assessment-intro">
        {/* Background effects */}
        <div className="fixed inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D0D0D] via-[#1a1a1a] to-[#0D0D0D]" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#CC5500]/10 rounded-full filter blur-[100px]" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#C5A065]/10 rounded-full filter blur-[100px]" />
        </div>

        <div className="relative z-10 max-w-3xl w-full text-center space-y-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
            {isStudent ? (
              <GraduationCap className="h-4 w-4 text-[#C5A065]" />
            ) : (
              <Briefcase className="h-4 w-4 text-[#CC5500]" />
            )}
            <span className="text-sm text-gray-400">
              {isStudent ? 'Career Discovery Assessment' : 'Executive Assessment'}
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight">
            {isStudent ? 'Career Discovery' : 'Leadership Intelligence'}
            <span className="block bg-gradient-to-r from-[#CC5500] to-[#C5A065] bg-clip-text text-transparent">Assessment</span>
          </h1>
          
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 sm:p-12 space-y-6 text-left">
            <p className="text-lg text-gray-300">
              {isStudent 
                ? 'This assessment helps you discover your natural interests, working style, and ideal career paths.'
                : 'This assessment decodes how you think, decide, and execute under real-world leadership conditions.'}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4">
              <div className="flex items-center space-x-3 text-gray-400 bg-white/5 rounded-xl p-4">
                <Brain className="h-5 w-5 text-[#CC5500] flex-shrink-0" />
                <span className="text-sm">{totalQuestions} {isStudent ? 'Questions' : 'Scenarios'}</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-400 bg-white/5 rounded-xl p-4">
                <Timer className="h-5 w-5 text-[#CC5500] flex-shrink-0" />
                <span className="text-sm">{estimatedMinutes} Minutes</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-400 bg-white/5 rounded-xl p-4">
                <Shield className="h-5 w-5 text-[#CC5500] flex-shrink-0" />
                <span className="text-sm">Encrypted</span>
              </div>
            </div>

            <div className="border-l-4 border-[#CC5500] pl-6 py-2 bg-[#CC5500]/5 rounded-r-xl">
              <p className="text-sm text-gray-400">
                <strong className="text-white">Tip:</strong> Answer instinctively based on how you would actually respond, not how you think you should respond.
              </p>
            </div>

            {/* Language Selector */}
            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-400">
                  <Globe className="h-4 w-4 text-[#C5A065]" />
                  <span className="text-sm">Assessment Language</span>
                </div>
                <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
                  <SelectTrigger className="w-[200px] bg-white/5 border-white/10 text-white" data-testid="language-selector">
                    <SelectValue placeholder="Select Language" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    {ASSESSMENT_LANGUAGES.map(lang => (
                      <SelectItem 
                        key={lang.code} 
                        value={lang.code}
                        className="text-white hover:bg-white/10 focus:bg-white/10"
                      >
                        {lang.nativeName} ({lang.name})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Choose your preferred language for the assessment questions
              </p>
            </div>
          </div>
          
          <Button
            onClick={startAssessment}
            disabled={loading || isTranslating}
            size="lg"
            className="bg-gradient-to-r from-[#CC5500] to-[#C5A065] hover:from-[#CC5500]/90 hover:to-[#C5A065]/90 text-white border-0 px-10 py-7 text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#CC5500]/30"
            data-testid="start-assessment-button"
          >
            {loading ? 'Initializing...' : isTranslating ? 'Translating...' : 'Begin Assessment'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    );
  }

  // Questions Phase
  if (phase === 'questions' && currentQuestion) {
    return (
      <div className="min-h-screen bg-[#0D0D0D]" data-testid="assessment-questions">
        {/* Background */}
        <div className="fixed inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D0D0D] via-[#1a1a1a] to-[#0D0D0D]" />
        </div>

        {/* Sticky Header with Timer and Progress */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-[#0D0D0D]/95 backdrop-blur-xl border-b border-white/10">
          <div className="max-w-4xl mx-auto px-4 py-4">
            {/* Timer and Stats Row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-4">
                {/* Timer - Countdown */}
                <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${remainingTime < 60 ? 'bg-red-500/20 border-red-500/30' : 'bg-white/5 border-white/10'}`}>
                  <Timer className={`h-4 w-4 ${remainingTime < 60 ? 'text-red-400' : 'text-[#CC5500]'}`} />
                  <span className={`font-mono text-lg ${remainingTime < 60 ? 'text-red-400' : 'text-white'}`}>{formatTime(remainingTime)}</span>
                </div>
                
                {/* Estimated remaining */}
                <div className="hidden sm:flex items-center gap-2 text-gray-500 text-sm">
                  <Clock className="h-4 w-4" />
                  <span>~{estimatedRemainingMinutes} min to complete</span>
                </div>
              </div>

              {/* Progress Stats */}
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm text-gray-500">Answered</p>
                  <p className="text-lg font-semibold text-white">{answeredCount}/{totalQuestions}</p>
                </div>
                <div className="w-16 h-16 relative">
                  <svg className="w-16 h-16 transform -rotate-90">
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      fill="none"
                      stroke="#1a1a1a"
                      strokeWidth="4"
                    />
                    <circle
                      cx="32"
                      cy="32"
                      r="28"
                      fill="none"
                      stroke="url(#progressGradient)"
                      strokeWidth="4"
                      strokeDasharray={`${2 * Math.PI * 28}`}
                      strokeDashoffset={`${2 * Math.PI * 28 * (1 - progress / 100)}`}
                      strokeLinecap="round"
                    />
                    <defs>
                      <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#CC5500" />
                        <stop offset="100%" stopColor="#C5A065" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold text-white">{Math.round(progress)}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="relative h-2 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#CC5500] to-[#C5A065] rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Question Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 pt-32 pb-24">
          <div className="mb-4">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#CC5500]/10 border border-[#CC5500]/20 text-[#CC5500] text-xs uppercase tracking-wider">
              {currentQuestion.phase.replace(/_/g, ' ')}
            </span>
          </div>

          {/* Question Card */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sm:p-10 mb-8">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-heading font-semibold text-white mb-4 leading-snug">
              {getQuestionText(currentQuestion)}
            </h2>
            {currentQuestion.subtitle && (
              <p className="text-gray-400">{currentQuestion.subtitle}</p>
            )}
          </div>

          {/* Answer Options */}
          <RadioGroup
            value={currentAnswer || ""}
            onValueChange={handleAnswer}
            className="space-y-3"
            data-testid="answer-options"
          >
            {currentQuestion.options.map((option, index) => (
              <div
                key={option.id}
                className={`group relative bg-white/5 border rounded-xl cursor-pointer transition-all duration-300 overflow-hidden ${
                  currentAnswer === option.id
                    ? 'border-[#CC5500] bg-[#CC5500]/10'
                    : 'border-white/10 hover:border-white/20 hover:bg-white/5'
                }`}
                onClick={() => handleAnswer(option.id)}
                data-testid={`answer-option-${option.id}`}
              >
                {/* Selection indicator */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 transition-all duration-300 ${
                  currentAnswer === option.id ? 'bg-gradient-to-b from-[#CC5500] to-[#C5A065]' : 'bg-transparent'
                }`} />
                
                <div className="flex items-start gap-4 p-5 pl-6">
                  <RadioGroupItem 
                    value={option.id} 
                    id={`${currentQuestion.id}-${option.id}`} 
                    className={`mt-0.5 border-2 ${
                      currentAnswer === option.id 
                        ? 'border-[#CC5500] text-[#CC5500]' 
                        : 'border-gray-600'
                    }`}
                    checked={currentAnswer === option.id}
                  />
                  <Label 
                    htmlFor={`${currentQuestion.id}-${option.id}`} 
                    className={`cursor-pointer leading-relaxed flex-1 transition-colors ${
                      currentAnswer === option.id ? 'text-white' : 'text-gray-400'
                    }`}
                  >
                    {getOptionText(currentQuestion, option)}
                  </Label>
                  {currentAnswer === option.id && (
                    <CheckCircle2 className="h-5 w-5 text-[#CC5500] flex-shrink-0" />
                  )}
                </div>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Fixed Bottom Navigation */}
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0D0D0D]/95 backdrop-blur-xl border-t border-white/10">
          <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
            <Button
              onClick={previousQuestion}
              disabled={currentQuestionIndex === 0}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="previous-question-button"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Previous
            </Button>

            <div className="text-center hidden sm:block">
              <p className="text-xs text-gray-500">Question</p>
              <p className="text-white font-semibold">{currentQuestionIndex + 1} of {totalQuestions}</p>
            </div>

            <Button
              onClick={nextQuestion}
              className="bg-gradient-to-r from-[#CC5500] to-[#C5A065] hover:from-[#CC5500]/90 hover:to-[#C5A065]/90 text-white border-0 min-w-[140px]"
              data-testid="next-question-button"
            >
              {currentQuestionIndex === totalQuestions - 1 ? 'Complete' : 'Next'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Processing Phase
  if (phase === 'processing') {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center px-4" data-testid="processing-screen">
        <div className="fixed inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D0D0D] via-[#1a1a1a] to-[#0D0D0D]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#CC5500]/20 rounded-full filter blur-[150px] animate-pulse" />
        </div>

        <div className="relative z-10 text-center space-y-8 max-w-md">
          {/* Animated loader */}
          <div className="relative w-24 h-24 mx-auto">
            <div className="absolute inset-0 border-4 border-[#CC5500]/20 rounded-full" />
            <div className="absolute inset-0 border-4 border-transparent border-t-[#CC5500] rounded-full animate-spin" />
            <div className="absolute inset-2 border-4 border-transparent border-t-[#C5A065] rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
          </div>

          <div>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Synthesizing Intelligence
            </h2>
            <p className="text-gray-400">
              Analyzing cognitive patterns, behavioral signatures, decision architecture...
            </p>
          </div>

          <div className="flex justify-center gap-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-2 h-2 bg-[#CC5500] rounded-full animate-bounce"
                style={{ animationDelay: `${i * 0.2}s` }}
              />
            ))}
          </div>

          <p className="text-sm text-gray-500">
            Completed in {formatTime(1800 - remainingTime)}
          </p>
        </div>
      </div>
    );
  }

  return null;
};
